// owned by WP-SEC-UI · "Still there?" two minutes before the 30-minute idle sign-out (shell-ia §11.2, security §3.3).
// A modal alert dialog (focus on "Stay signed in"), a countdown read out every 15 s and every second in the last 10 s, and the two
// choices. Any activity in any tab closes it: the idle clock is shared. "Stay signed in" renews the session, which counts as activity.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LogOut, TimerReset } from "lucide-react";
import { useAuth, useSessionClock } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { countdownText } from "./useSessionInfo";

export const IDLE_WARNING_MS = 2 * 60_000;

export function IdleDialog() {
  const { t } = useTranslation();
  const { refresh, signOut, managed } = useAuth();
  const { idleDeadline } = useSessionClock();
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const stay = useRef<HTMLButtonElement>(null);
  const leave = useRef<HTMLButtonElement>(null);
  const left = idleDeadline === null ? Number.POSITIVE_INFINITY : idleDeadline - now;
  const open = managed && left > 0 && left <= IDLE_WARNING_MS;

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    stay.current?.focus();
    return () => previous?.focus?.();
  }, [open]);

  if (!open) return null;

  // Read out every 15 s, then every second for the last 10 s.
  const secs = Math.ceil(left / 1000);
  const spoken = secs <= 10 ? secs : Math.ceil(secs / 15) * 15;
  const spokenText = t("assistant:econic.session.idle.countdown", { time: countdownText(spoken * 1000) });

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      void onStay();
    }
    if (e.key === "Tab") {
      e.preventDefault();
      (document.activeElement === stay.current ? leave.current : stay.current)?.focus();
    }
  };
  const onStay = async () => {
    setBusy(true);
    try {
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[75] grid place-items-center p-4" data-session-warning="idle">
      {/* The scrim sits under the dialog inside this layer (its own z-index 55 would otherwise cover the buttons). */}
      <div className="ec-scrim" style={{ zIndex: 0 }} aria-hidden />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="econic-idle-title"
        aria-describedby="econic-idle-body"
        data-session-prompt=""
        onKeyDown={onKeyDown}
        className="ec-dialog relative z-[1] w-full max-w-[420px] text-start"
      >
        <div className="flex items-start gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[var(--st-warn-bg)] text-[var(--st-warn)]">
            <TimerReset className="size-5" aria-hidden strokeWidth={1.75} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="econic-idle-title" className="t-h2 m-0 text-ink">{t("assistant:econic.session.idle.title")}</h2>
            <p id="econic-idle-body" className="t-body mt-1 text-ink-2">{t("assistant:econic.session.idle.body")}</p>
          </div>
        </div>
        <p className="mt-4 flex items-baseline justify-between gap-3 rounded-[10px] border border-hair bg-surface-2 px-3 py-2.5" aria-hidden>
          <span className="t-small text-ink-3">{t("assistant:econic.session.idle.countdown", { time: "" }).trim()}</span>
          <bdi dir="ltr" className="t-num-m tnum font-semibold text-ink">{countdownText(left)}</bdi>
        </p>
        <span className="sr-only" aria-live="assertive" aria-atomic="true">{spokenText}</span>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button ref={leave} type="button" className="ec-btn secondary" onClick={() => signOut("signed_out")}>
            <LogOut aria-hidden className="rtl:-scale-x-100" /> {t("assistant:econic.session.idle.leave")}
          </button>
          <button ref={stay} type="button" className="ec-btn primary" disabled={busy} onClick={() => void onStay()}>
            {t("assistant:econic.session.idle.stay")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
